import React, { FC, useMemo, useCallback, memo } from 'react';
import { useFormikContext } from 'formik';
import _isBoolean from 'lodash/isBoolean';
import _findIndex from 'lodash/findIndex';
import { useTranslation } from '@blocs.i18n';
import { RangeEnum, FilterData, SelectOption } from '../../../../shared/types';
import { getOptionsWithEmptyOption, useMeasure } from '../../../../shared/utils';
import { FieldGroup, Delimiter, Label } from '../../../../shared/styles';
import { StyledFormikSelect } from '../../styles';

type Props = {
  saveAsOption?: boolean;
  showInImperial?: boolean;
  rangeName: FilterData['name'];
  minName: FilterData['name'];
  maxName: FilterData['name'];
  label?: FilterData['label'];
  options: SelectOption[];
};

const RangeSelector: FC<Props> = ({ saveAsOption, showInImperial, rangeName, minName, maxName, label, options }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext();

  const selectedMinOption = values[rangeName][minName];
  const selectedMaxOption = values[rangeName][maxName];

  const { minOptions, maxOptions } = useMemo(() => {
    const optionsWithEmptyOption = getOptionsWithEmptyOption(options);

    const selectedMinOptionIndex = _findIndex(
      optionsWithEmptyOption,
      (option) => selectedMinOption.value && option.value === selectedMinOption.value
    );
    const selectedMaxOptionIndex = _findIndex(
      optionsWithEmptyOption,
      (option) => selectedMaxOption.value && option.value === selectedMaxOption.value
    );

    return {
      minOptions:
        selectedMaxOptionIndex !== -1
          ? optionsWithEmptyOption.slice(0, selectedMaxOptionIndex + 1)
          : optionsWithEmptyOption,
      maxOptions:
        selectedMinOptionIndex !== -1
          ? getOptionsWithEmptyOption(optionsWithEmptyOption.slice(selectedMinOptionIndex))
          : optionsWithEmptyOption,
    };
  }, [selectedMinOption, selectedMaxOption, options]);

  const measure = useMeasure({ switchTo: !showInImperial, type: RangeEnum.size });

  const renderLabelContent = useCallback(() => {
    if (_isBoolean(showInImperial)) {
      return `${label} (${measure})`;
    }

    return label;
  }, []);

  return (
    <>
      {label && <Label>{renderLabelContent()}</Label>}
      <FieldGroup>
        <StyledFormikSelect
          saveAsOption={saveAsOption}
          name={`${rangeName}.${minName}`}
          size="small"
          options={minOptions}
          data-testid="range-selector-1"
        />
        <Delimiter>{t('common:filter.to')}</Delimiter>
        <StyledFormikSelect
          saveAsOption={saveAsOption}
          name={`${rangeName}.${maxName}`}
          size="small"
          options={maxOptions}
          data-testid="range-selector-2"
        />
      </FieldGroup>
    </>
  );
};

export default memo(RangeSelector);
